import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Loader2, ShieldCheck, AlertCircle, ArrowRight, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";
import { postNativeRequest } from "@/lib/api";

/**
 * AP-5B.2 — `/signin` — Native AssosPay magic-link entry point.
 *
 * Single combined sign-in surface. Email field + "Send me a magic link"
 * button. On success the user is redirected to `/signin/sent` with the
 * (optional) preview `debug_token` carried via navigation state — never
 * via localStorage / sessionStorage / URL.
 *
 * Strict rules (mirror backend contract):
 *   - NEVER store the raw token anywhere persistent.
 *   - NEVER print the email of an arbitrary user to the console / analytics.
 *   - Always show enumeration-safe copy ("If this email can sign in…").
 *   - 429 → render an explicit rate-limit error.
 *   - No password input, no wallet signature, no SIWE.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RETURN_URL_DEFAULT = "/account";

function mapError(err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.detail?.error;
  if (status === 429 || code === "rate_limited") {
    return {
      title: "Too many requests",
      detail:
        "We received too many sign-in attempts from this email or device. Please wait a few minutes and try again.",
      kind: "rate_limited",
    };
  }
  return {
    title: "We couldn't reach AssosPay",
    detail:
      "Something interrupted the sign-in request. Please try again in a moment.",
    kind: "network",
  };
}

export default function Signin() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const trimmed = email.trim();
  const emailValid = EMAIL_RE.test(trimmed);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !emailValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const out = await postNativeRequest({
        email: trimmed,
        returnUrl: RETURN_URL_DEFAULT,
      });
      // Carry debug_token (if any) + return_url via navigation state.
      // Never write the token to localStorage / sessionStorage / URL.
      navigate("/signin/sent", {
        replace: true,
        state: {
          debugToken: out?.debug_token || null,
          returnUrl: out?.return_url || RETURN_URL_DEFAULT,
        },
      });
    } catch (e) {
      setError(mapError(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="signin-page">
      <Header />
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />
        <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          <div className="mb-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#00F0FF]">
              AssosPay · ArenaCoin
            </p>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-black text-white">
              Sign in
            </h1>
            <p className="mt-2 font-mono text-xs text-white/60 max-w-xl">
              Enter your email to receive a one-time magic link. AssosPay
              never stores a password and never asks you to sign with
              your wallet to sign in.
            </p>
          </div>

          {/* Magic-link form */}
          <form
            onSubmit={handleSubmit}
            className="glass rounded-md p-6 sm:p-8"
            data-testid="signin-form"
          >
            <label
              htmlFor="signin-email"
              className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60"
            >
              Email
            </label>
            <div className="mt-2 flex items-center gap-2 rounded-md border border-white/10 bg-black/40 px-3 py-2 focus-within:border-[#00F0FF]/60 transition-colors">
              <Mail className="h-4 w-4 text-white/40" />
              <input
                id="signin-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder-white/30"
                data-testid="signin-email-input"
                aria-invalid={trimmed.length > 0 && !emailValid}
                disabled={submitting}
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !emailValid}
              data-testid="signin-submit-button"
              className="btn-primary text-xs mt-5 flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Send me a magic link
            </button>

            <p
              className="mt-4 font-mono text-[11px] text-white/50"
              data-testid="signin-enumeration-note"
            >
              If this email can sign in, we'll send a one-time link valid
              for 15 minutes. The same response is returned whether or
              not an account exists.
            </p>

            {error && (
              <div
                className="mt-5 flex items-start gap-2 rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2"
                data-testid="signin-error"
                data-error-kind={error.kind}
              >
                <AlertCircle className="h-4 w-4 text-[#FF3B30] mt-0.5" />
                <div>
                  <p className="font-display text-xs uppercase tracking-[0.2em] text-[#FF3B30]">
                    {error.title}
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-white/70">
                    {error.detail}
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Assos365 alternate path */}
          <div
            className="mt-6 flex items-center justify-between gap-4 rounded-md border border-white/10 bg-black/40 px-5 py-4"
            data-testid="signin-assos365-strip"
          >
            <div>
              <p className="font-display text-xs uppercase tracking-[0.2em] text-white">
                Already have an Assos365 account?
              </p>
              <p className="mt-1 font-mono text-[11px] text-white/55">
                Use the existing partner deep-link flow to land your
                AssosPay session.
              </p>
            </div>
            <Assos365LinkButton />
          </div>

          {/* Security strip */}
          <div
            className="mt-6 flex items-center gap-2 text-white/45"
            data-testid="signin-security-strip"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#00F0FF]" />
            <span className="font-mono text-[11px]">
              No password · No wallet signature · One-time link only
            </span>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Assos365 link entry button.
//
// Production wiring: the deeplink trip MUST start on the Assos365 side
// (Assos365 mints an HS256 v2 JWT, then redirects to
// `<AssosPay>/link/callback?link_token=…`). The button below therefore:
//
//   * IF `REACT_APP_ASSOS365_LINK_START_URL` is configured → renders as
//     an external <a> pointing at Assos365 with a `return_to` query
//     param the partner is expected to honour.
//   * ELSE → renders as a clearly disabled button with explanatory
//     copy. Crucially it never points at the bare `/link/callback`
//     route, which would dead-end on the "missing token" view (the
//     2026-02-12 production UX regression).
//
// NO redemption submit. NO `/credit`. NO wallet signature. NO env flip.
// ---------------------------------------------------------------------------
function Assos365LinkButton() {
  // NOTE: CRA/webpack DefinePlugin replaces `process.env.REACT_APP_*` with
  // a string literal at build time. A `typeof process !== "undefined"`
  // guard around it is a footgun: at runtime in the browser `process`
  // is undefined, so the guard collapses to the empty branch and the
  // button stays disabled even when the env var is set. Reference the
  // build-time value directly.
  const startUrl = process.env.REACT_APP_ASSOS365_LINK_START_URL || "";
  if (startUrl) {
    const returnTo = `${window.location.origin}/link/callback`;
    const href = `${startUrl}${startUrl.includes("?") ? "&" : "?"}return_to=${encodeURIComponent(returnTo)}`;
    return (
      <a
        href={href}
        rel="noopener noreferrer"
        className="btn-secondary text-xs flex items-center gap-2"
        data-testid="signin-assos365-link"
        data-link-mode="enabled"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Link with Assos365
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled
      className="btn-secondary text-xs flex items-center gap-2 opacity-60 cursor-not-allowed"
      title="Assos365 link entry URL is not configured for this deployment."
      data-testid="signin-assos365-link"
      data-link-mode="disabled"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Link with Assos365
    </button>
  );
}
